/**
 * Authentication context provider
 * Updated for custom JWT authentication system
 *
 * @author Claude
 */
import { createContext, ReactNode, useEffect, useState } from 'react';
import { AuthContextType, AuthState, LoginCredentials, RegisterData, User } from '../types/auth';
import * as authService from '../services/authService';

// Default auth state
const defaultAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: true,
};

// Create auth context
export const AuthContext = createContext<AuthContextType>({
  ...defaultAuthState,
  login: async () => {},
  register: async () => {},
  acceptInvite: async () => {},
  logout: async () => {},
  refreshAuth: async () => false,
  verifyEmail: async () => {},
  resendVerification: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Auth context provider component
 * @param {AuthProviderProps} props - Component props
 * @returns {JSX.Element} Auth provider component
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authState, setAuthState] = useState<AuthState>(defaultAuthState);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const refreshToken = localStorage.getItem('auth_refresh_token');
        const userStr = localStorage.getItem('auth_user');

        if (token && userStr) {
          try {
            const user = JSON.parse(userStr) as User;
            setAuthState({
              isAuthenticated: true,
              user,
              token,
              refreshToken,
              loading: false,
            });
          } catch (parseError) {
            console.error('Failed to parse user data:', parseError);
            clearAuthData();
          }
        } else {
          // No stored auth data
          setAuthState({
            ...defaultAuthState,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        clearAuthData();
      }
    };

    initializeAuth();
  }, []);

  // Store auth data in localStorage
  const saveAuthData = (token: string, refreshToken: string, user: User) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_refresh_token', refreshToken);
    localStorage.setItem('auth_user', JSON.stringify(user));
  };

  // Clear auth data from localStorage
  const clearAuthData = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_refresh_token');
    localStorage.removeItem('auth_user');

    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      refreshToken: null,
      loading: false,
    });
  };

  // Accept invite function
  const acceptInvite = async (token: string, password: string) => {
    try {
      const response = await authService.acceptInvite(token, password);

      // Save auth data
      saveAuthData(response.data.access_token, response.data.refresh_token, response.data.user);

      // Update state
      setAuthState({
        isAuthenticated: true,
        user: response.data.user,
        token: response.data.access_token,
        refreshToken: response.data.refresh_token,
        loading: false,
      });
    } catch (error) {
      console.error('Accept invite failed:', error);
      throw error;
    }
  };

  // Register function
  const register = async (data: RegisterData) => {
    try {
      const response = await authService.register(data);

      // Save auth data
      saveAuthData(response.data.access_token, response.data.refresh_token, response.data.user);

      // Update state
      setAuthState({
        isAuthenticated: true,
        user: response.data.user,
        token: response.data.access_token,
        refreshToken: response.data.refresh_token,
        loading: false,
      });
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  // Login function
  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);

      // Save auth data
      saveAuthData(response.data.access_token, response.data.refresh_token, response.data.user);

      // Update state
      setAuthState({
        isAuthenticated: true,
        user: response.data.user,
        token: response.data.access_token,
        refreshToken: response.data.refresh_token,
        loading: false,
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      if (authState.token) {
        await authService.logout(authState.token);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
    }
  };

  // Verify email function
  const verifyEmail = async (token: string) => {
    try {
      await authService.verifyEmail(token);

      // Update user's email_verified status
      if (authState.user) {
        const updatedUser = { ...authState.user, email_verified: true };
        localStorage.setItem('auth_user', JSON.stringify(updatedUser));
        setAuthState(prev => ({
          ...prev,
          user: updatedUser,
        }));
      }
    } catch (error) {
      console.error('Email verification failed:', error);
      throw error;
    }
  };

  // Resend verification function
  const resendVerification = async () => {
    try {
      if (!authState.token) {
        throw new Error('Not authenticated');
      }
      await authService.resendVerification(authState.token);
    } catch (error) {
      console.error('Resend verification failed:', error);
      throw error;
    }
  };

  // Forgot password function
  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      console.error('Forgot password failed:', error);
      throw error;
    }
  };

  // Reset password function
  const resetPassword = async (token: string, password: string) => {
    try {
      await authService.resetPassword(token, password);
    } catch (error) {
      console.error('Reset password failed:', error);
      throw error;
    }
  };

  // Refresh authentication
  const refreshAuth = async (): Promise<boolean> => {
    try {
      if (!authState.refreshToken) {
        return false;
      }

      const response = await authService.refreshToken(authState.refreshToken);

      if (response.data.access_token) {
        // Save new tokens
        localStorage.setItem('auth_token', response.data.access_token);
        localStorage.setItem('auth_refresh_token', response.data.refresh_token);

        // Update state
        setAuthState(prev => ({
          ...prev,
          isAuthenticated: true,
          token: response.data.access_token,
          refreshToken: response.data.refresh_token,
          loading: false,
        }));

        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuthData();
      return false;
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    acceptInvite,
    login,
    register,
    logout,
    refreshAuth,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
